import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Trash2,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  Package,
  Map,
  Filter,
  Download,
  Paintbrush,
  Clock,
  ImageOff,
  ExternalLink,
  Settings2,
  SlidersHorizontal,
  Share2,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { feedWatchService } from '../services/feedWatch';
import { socialPostingService } from '../services/socialPosting';
import type { ProductInput } from '../services/socialPosting';
import { StatusBadge } from '../components/feedwatch/FeedList';
import { ShopList } from '../components/feedwatch/ShopList';
import { ShopDetail } from '../components/feedwatch/ShopDetail';
import { MappingBuilder } from '../components/feedwatch/MappingBuilder';
import { FilterBuilder } from '../components/feedwatch/FilterBuilder';
import { ExportManager } from '../components/feedwatch/ExportManager';
import { CanvasManager } from '../components/feedwatch/canvas/CanvasManager';
import { ScheduleManager } from '../components/feedwatch/ScheduleManager';
import {
  ColumnSettingsDialog,
  loadColumnSettings,
  type ColumnSettings,
} from '../components/feedwatch/ColumnSettings';
import {
  ProductFilterBar,
  applyProductFilters,
  type ProductFilterRule,
} from '../components/feedwatch/ProductFilter';
import type { Shop, Feed, FeedProduct, ShopPlatform } from '../services/feedWatch';

// ── Sub-Navigation Sections ──────────────────────────────────────

type Section = 'products' | 'mappings' | 'filters' | 'exports' | 'canvas' | 'schedules';

interface SectionItem { key: Section; label: string; icon: React.ElementType }

const DATA_SECTIONS: SectionItem[] = [
  { key: 'products', label: 'Products', icon: Package },
  { key: 'mappings', label: 'Mappings', icon: Map },
  { key: 'filters', label: 'Filters', icon: Filter },
  { key: 'exports', label: 'Exports', icon: Download },
];

const CREATIVE_SECTIONS: SectionItem[] = [
  { key: 'canvas', label: 'Dynamic Templates', icon: Paintbrush },
  { key: 'schedules', label: 'Schedules', icon: Clock },
];

// ── Smart Cell Helpers ───────────────────────────────────────────

const COLUMN_PRIORITY: RegExp[] = [
  /image/i,
  /title|name/i,
  /^id$|sku|gtin|mpn/i,
  /price/i,
  /link|url/i,
  /availability/i,
  /brand/i,
  /description/i,
];

function sortColumns(keys: string[]): string[] {
  const sorted: string[] = [];
  const remaining = new Set(keys);

  for (const pattern of COLUMN_PRIORITY) {
    for (const key of keys) {
      if (remaining.has(key) && pattern.test(key)) {
        sorted.push(key);
        remaining.delete(key);
      }
    }
  }

  for (const key of keys) {
    if (remaining.has(key)) {
      sorted.push(key);
    }
  }

  return sorted;
}

function formatHeader(key: string): string {
  return key
    .replace(/^g:/i, '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isUrl(value: unknown): value is string {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

/** Returns class for the <div> wrapper inside each <td> — controls overflow */
function cellWrapperClass(key: string, value: unknown): string {
  if (/image/i.test(key)) return '';
  if (/availability|condition/i.test(key)) return 'whitespace-nowrap';
  if (/price/i.test(key)) return 'whitespace-nowrap';
  if (/title|name|description/i.test(key)) {
    const str = String(value ?? '');
    return str.length > 100
      ? 'max-w-[280px]'
      : 'max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap';
  }
  if (isUrl(value)) return 'max-w-[200px] overflow-hidden';
  return 'max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap';
}

/** Wraps truncated text in a tooltip that shows the full value on hover */
function TextWithTooltip({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">{children}</span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-sm whitespace-normal break-words text-xs leading-relaxed bg-popover text-popover-foreground border shadow-lg"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function renderCellValue(key: string, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground select-none">—</span>;
  }

  const str = String(value);

  // Image thumbnail — handle arrays, comma-separated, or single URL
  if (/image/i.test(key)) {
    const firstUrl = Array.isArray(value)
      ? String(value[0] ?? '').trim()
      : str.split(',')[0].trim();
    if (isUrl(firstUrl)) {
      const count = Array.isArray(value) ? value.length : str.split(',').length;
      return (
        <div className="flex items-center gap-2">
          <ImageCell src={firstUrl} />
          {count > 1 && (
            <span className="text-xs text-muted-foreground">+{count - 1}</span>
          )}
        </div>
      );
    }
  }

  // URL link (handle arrays — use first element)
  const urlCandidate = Array.isArray(value) ? String(value[0] ?? '') : str;
  if (isUrl(urlCandidate)) {
    let display: string;
    try {
      display = new URL(urlCandidate).hostname;
    } catch {
      display = urlCandidate.slice(0, 30);
    }
    return (
      <TextWithTooltip text={urlCandidate}>
        <a
          href={urlCandidate}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-primary hover:underline min-w-0"
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">{display}</span>
          <ExternalLink className="h-3 w-3 shrink-0 opacity-50" />
        </a>
      </TextWithTooltip>
    );
  }

  // Price
  if (/price/i.test(key)) {
    return <span className="font-semibold tabular-nums">{str}</span>;
  }

  // Availability badge
  if (/availability/i.test(key)) {
    const lower = str.toLowerCase();
    const color = lower.includes('in stock') || lower.includes('in_stock')
      ? 'bg-green-500/15 text-green-400 border-green-500/30'
      : lower.includes('out') || lower.includes('out_of_stock')
        ? 'bg-red-500/15 text-red-400 border-red-500/30'
        : 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30';
    return <Badge variant="outline" className={color}>{str}</Badge>;
  }

  // Condition badge
  if (/condition/i.test(key)) {
    const lower = str.toLowerCase();
    const color = lower === 'new'
      ? 'bg-green-500/15 text-green-400 border-green-500/30'
      : lower === 'used'
        ? 'bg-orange-500/15 text-orange-400 border-orange-500/30'
        : 'bg-blue-500/15 text-blue-400 border-blue-500/30';
    return <Badge variant="outline" className={color}>{str}</Badge>;
  }

  // Long text — tooltip with full text
  if (str.length > 80) {
    return (
      <TextWithTooltip text={str}>
        <span className="line-clamp-2 whitespace-normal">
          {str.length > 300 ? `${str.slice(0, 300)}…` : str}
        </span>
      </TextWithTooltip>
    );
  }

  // Default — truncated text with tooltip when it may be clipped
  if (str.length > 30) {
    return (
      <TextWithTooltip text={str}>
        <span>{str.length > 120 ? `${str.slice(0, 120)}…` : str}</span>
      </TextWithTooltip>
    );
  }

  return <>{str}</>;
}

function ImageCell({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="w-10 h-10 rounded-lg bg-muted/60 flex items-center justify-center">
        <ImageOff className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <img
            src={src}
            alt=""
            className="w-10 h-10 rounded-lg object-cover bg-muted/60 cursor-pointer"
            loading="lazy"
            onError={() => setFailed(true)}
          />
        </TooltipTrigger>
        <TooltipContent
          side="right"
          sideOffset={8}
          className="p-2 bg-popover border shadow-2xl rounded-xl"
        >
          <img
            src={src}
            alt=""
            className="rounded-lg object-contain bg-muted/30"
            style={{ width: 280, height: 280 }}
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Products Section ─────────────────────────────────────────────

function ProductsSection({ feedId, detectedFields }: { feedId: string; detectedFields: string[] }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState<FeedProduct[]>([]);
  const [meta, setMeta] = useState<{ total: number; page: number; limit: number }>({ total: 0, page: 1, limit: 50 });
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  // Selection state for Social Posting
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSendingToPost, setIsSendingToPost] = useState(false);
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [postDialogMode, setPostDialogMode] = useState<'new' | 'existing'>('new');
  const [postName, setPostName] = useState('');
  const [existingPosts, setExistingPosts] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedExistingPostId, setSelectedExistingPostId] = useState('');
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);

  // Column settings
  const [columnSettings, setColumnSettings] = useState<ColumnSettings | null>(
    () => loadColumnSettings(feedId),
  );
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // Inline product data filters (client-side)
  const [showFilters, setShowFilters] = useState(false);
  const [productFilters, setProductFilters] = useState<ProductFilterRule[]>([]);

  const loadProducts = useCallback(async (page: number, searchTerm?: string) => {
    try {
      setIsLoading(true);
      const result = await feedWatchService.listProducts(feedId, page, 50, searchTerm);
      setProducts(result.data);
      setMeta(result.meta);
    } catch {
      // silently fail
    } finally {
      setIsLoading(false);
    }
  }, [feedId]);

  useEffect(() => {
    loadProducts(1);
  }, [loadProducts]);

  const handleSearch = (value: string) => {
    setSearch(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      loadProducts(1, value || undefined);
    }, 400);
  };

  // All raw_data keys from first product
  const allKeys = useMemo(
    () => (products.length > 0 ? Object.keys(products[0].raw_data) : []),
    [products],
  );

  // Apply client-side filters to loaded products
  const filteredProducts = useMemo(
    () => applyProductFilters(products, productFilters),
    [products, productFilters],
  );

  const columns = useMemo(() => {
    if (columnSettings) {
      const visible = columnSettings.order.filter(
        (k) => allKeys.includes(k) && !columnSettings.hidden.includes(k),
      );
      const newKeys = allKeys.filter((k) => !columnSettings.order.includes(k));
      return [...visible, ...newKeys];
    }
    return sortColumns(allKeys);
  }, [allKeys, columnSettings]);

  // Count only non-empty filter rules
  const activeFilterCount = productFilters.filter((f) => f.field).length;
  const totalPages = Math.ceil(meta.total / meta.limit);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === filteredProducts.length
        ? new Set()
        : new Set(filteredProducts.map((p) => p.id))
    );
  }, [filteredProducts]);

  const loadExistingPosts = useCallback(async () => {
    try {
      setIsLoadingPosts(true);
      const result = await socialPostingService.listPosts(1, 100);
      setExistingPosts(result.data.map((p) => ({ id: p.id, name: p.name })));
    } catch {
      // handled
    } finally {
      setIsLoadingPosts(false);
    }
  }, []);

  const openPostDialog = useCallback((mode: 'new' | 'existing') => {
    setPostDialogMode(mode);
    setPostName('');
    setSelectedExistingPostId('');
    setShowPostDialog(true);
    if (mode === 'existing') {
      loadExistingPosts();
    }
  }, [loadExistingPosts]);

  const handleSendToSocialPost = useCallback(async () => {
    try {
      setIsSendingToPost(true);
      const selectedProducts: ProductInput[] = filteredProducts
        .filter((p) => selectedIds.has(p.id))
        .map((p, i) => ({
          feed_product_id: p.id,
          feed_id: feedId,
          sort_order: i,
          raw_data: p.raw_data,
        }));

      let targetPostId: string;

      if (postDialogMode === 'new') {
        if (!postName.trim()) return;
        const post = await socialPostingService.createPost({ name: postName.trim() });
        targetPostId = post.id;
      } else {
        if (!selectedExistingPostId) return;
        targetPostId = selectedExistingPostId;
      }

      await socialPostingService.addProducts(targetPostId, selectedProducts);
      setSelectedIds(new Set());
      setShowPostDialog(false);
      setPostName('');
      navigate(`/social-posting?postId=${targetPostId}`);
    } catch {
      // error handled by service
    } finally {
      setIsSendingToPost(false);
    }
  }, [postName, postDialogMode, selectedExistingPostId, filteredProducts, selectedIds, feedId, navigate]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Search + count */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search products..."
            className="pl-10 h-11 rounded-xl border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{meta.total.toLocaleString()}</span> products
          {activeFilterCount > 0 && filteredProducts.length !== products.length && (
            <span className="ml-1">
              (<span className="font-semibold text-foreground">{filteredProducts.length}</span> shown)
            </span>
          )}
        </div>
      </div>

      {/* Toolbar: Filter + Columns */}
      <div className="flex items-center gap-2">
        <Button
          variant={showFilters ? 'default' : 'outline'}
          size="sm"
          className="rounded-lg"
          onClick={() => setShowFilters((v) => !v)}
        >
          <SlidersHorizontal className="h-4 w-4 mr-1.5" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1.5 h-5 min-w-[20px] px-1.5 text-xs rounded-full">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="rounded-lg"
          onClick={() => setShowColumnSettings(true)}
        >
          <Settings2 className="h-4 w-4 mr-1.5" />
          Columns
        </Button>
      </div>

      {/* Collapsible inline data filters */}
      {showFilters && (
        <Card className="border-2 rounded-xl p-4 animate-in slide-in-from-top-2 duration-200">
          <ProductFilterBar
            filters={productFilters}
            onChange={setProductFilters}
            fields={allKeys.length > 0 ? allKeys : detectedFields}
            formatHeader={formatHeader}
          />
        </Card>
      )}

      {/* Column settings dialog */}
      <ColumnSettingsDialog
        open={showColumnSettings}
        onOpenChange={setShowColumnSettings}
        feedId={feedId}
        allColumns={allKeys.length > 0 ? allKeys : detectedFields}
        settings={columnSettings}
        onSettingsChange={setColumnSettings}
        formatHeader={formatHeader}
      />

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-24">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {activeFilterCount > 0 ? 'No products match your filters.' : 'No products found.'}
          </p>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" className="mt-3" onClick={() => setProductFilters([])}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <Card className="border-2 shadow-lg rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-3 sticky left-0 bg-muted/50 z-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredProducts.length && filteredProducts.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                    #
                  </th>
                  {columns.map((key) => (
                    <th
                      key={key}
                      className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap"
                    >
                      {formatHeader(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className={`hover:bg-muted/30 transition-colors duration-150 ${selectedIds.has(product.id) ? 'bg-primary/5' : ''}`}>
                    <td className="px-3 py-3 sticky left-0 bg-background z-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(product.id)}
                        onChange={() => toggleSelect(product.id)}
                        className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground tabular-nums whitespace-nowrap">
                      {product.sort_index}
                    </td>
                    {columns.map((key) => {
                      const val = product.raw_data[key];
                      return (
                        <td key={key} className="px-4 py-3 text-sm align-middle">
                          <div className={cellWrapperClass(key, val)}>
                            {renderCellValue(key, val)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg"
            disabled={meta.page <= 1}
            onClick={() => loadProducts(meta.page - 1, search || undefined)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums">
            Page <span className="font-medium text-foreground">{meta.page}</span> of <span className="font-medium text-foreground">{totalPages}</span>
          </span>
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg"
            disabled={meta.page >= totalPages}
            onClick={() => loadProducts(meta.page + 1, search || undefined)}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Floating Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-primary text-primary-foreground shadow-2xl rounded-xl px-5 py-3 flex items-center gap-4 animate-in slide-in-from-bottom-4 duration-300">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <Button
            size="sm"
            variant="secondary"
            className="rounded-lg"
            onClick={() => openPostDialog('new')}
          >
            <Share2 className="h-4 w-4 mr-1.5" />
            New Social Post
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="rounded-lg"
            onClick={() => openPostDialog('existing')}
          >
            <Package className="h-4 w-4 mr-1.5" />
            Add to Existing Post
          </Button>
          <button onClick={() => setSelectedIds(new Set())} className="ml-1 hover:bg-primary-foreground/20 rounded-lg p-1 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Post Dialog (New or Existing) */}
      {showPostDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-xl border-2 shadow-2xl p-6 w-full max-w-md space-y-4 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-semibold">
              {postDialogMode === 'new' ? 'Create Social Post' : 'Add to Existing Post'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {selectedIds.size} product{selectedIds.size > 1 ? 's' : ''} will be added.
            </p>

            {postDialogMode === 'new' ? (
              <Input
                value={postName}
                onChange={(e) => setPostName(e.target.value)}
                placeholder="Post name..."
                className="h-11 rounded-xl"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleSendToSocialPost(); }}
              />
            ) : isLoadingPosts ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : existingPosts.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No existing posts found. Create a new one instead.
              </p>
            ) : (
              <select
                value={selectedExistingPostId}
                onChange={(e) => setSelectedExistingPostId(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                autoFocus
              >
                <option value="">Select a post...</option>
                {existingPosts.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="outline" className="rounded-lg" onClick={() => setShowPostDialog(false)}>
                Cancel
              </Button>
              <Button
                className="rounded-lg"
                onClick={handleSendToSocialPost}
                disabled={
                  isSendingToPost ||
                  (postDialogMode === 'new' ? !postName.trim() : !selectedExistingPostId)
                }
              >
                {isSendingToPost && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                {postDialogMode === 'new' ? 'Create Post' : 'Add Products'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Feed Detail View ─────────────────────────────────────────────

interface FeedDetailProps {
  feed: Feed;
  onBack: () => void;
  onFeedUpdated: (feed: Feed) => void;
  onDeleted: () => void;
  shopName?: string;
  onBackToShops?: () => void;
}

function FeedDetail({ feed, onBack, onFeedUpdated, onDeleted, shopName, onBackToShops }: FeedDetailProps) {
  const [currentFeed, setCurrentFeed] = useState(feed);
  const [activeSection, setActiveSection] = useState<Section>('products');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (currentFeed.status === 'pending' || currentFeed.status === 'fetching') {
      pollRef.current = setInterval(async () => {
        try {
          const updated = await feedWatchService.getFeed(currentFeed.id);
          setCurrentFeed(updated);
          onFeedUpdated(updated);
          if (updated.status !== 'pending' && updated.status !== 'fetching') {
            if (pollRef.current) clearInterval(pollRef.current);
          }
        } catch {
          // keep polling
        }
      }, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [currentFeed.id, currentFeed.status, onFeedUpdated]);

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      const updated = await feedWatchService.refreshFeed(currentFeed.id);
      setCurrentFeed(updated);
      onFeedUpdated(updated);
    } catch {
      // handled
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this collection and all its data?')) return;
    try {
      setIsDeleting(true);
      await feedWatchService.deleteFeed(currentFeed.id);
      onDeleted();
    } catch {
      setIsDeleting(false);
    }
  };

  const renderNotReady = () => {
    if (currentFeed.status === 'pending' || currentFeed.status === 'fetching') {
      return (
        <div className="flex flex-col items-center gap-4 py-24">
          <Loader2 className="h-10 w-10 animate-spin text-primary/60" />
          <p className="text-muted-foreground">Collection is being fetched...</p>
        </div>
      );
    }
    if (currentFeed.status === 'error') {
      return (
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="text-destructive">Collection fetch failed: {currentFeed.error_message}</p>
          <Button variant="outline" onClick={handleRefresh}>Retry</Button>
        </div>
      );
    }
    return (
      <div className="text-center py-24 text-muted-foreground">
        Collection is stale. Refresh to fetch latest data.
      </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'products':
        return currentFeed.status === 'ready'
          ? <ProductsSection feedId={currentFeed.id} detectedFields={[...currentFeed.detected_fields]} />
          : renderNotReady();
      case 'mappings':
        return currentFeed.status === 'ready'
          ? <MappingBuilder feedId={currentFeed.id} detectedFields={[...currentFeed.detected_fields]} feedType={currentFeed.feed_type} />
          : <div className="text-center py-24 text-muted-foreground">Collection must be in &quot;ready&quot; status to configure mappings.</div>;
      case 'filters':
        return currentFeed.status === 'ready'
          ? <FilterBuilder feedId={currentFeed.id} detectedFields={[...currentFeed.detected_fields]} />
          : <div className="text-center py-24 text-muted-foreground">Collection must be in &quot;ready&quot; status to configure filters.</div>;
      case 'exports':
        return <ExportManager feedId={currentFeed.id} feedStatus={currentFeed.status} feedType={currentFeed.feed_type} />;
      case 'canvas':
        return currentFeed.status === 'ready'
          ? <CanvasManager
              feedId={currentFeed.id}
              detectedFields={[...currentFeed.detected_fields]}
              totalProducts={currentFeed.total_products}
              breadcrumbSegments={[
                ...(shopName ? [{ label: shopName, onClick: onBackToShops }] : []),
                { label: currentFeed.name, onClick: () => setActiveSection('products') },
              ]}
            />
          : <div className="text-center py-24 text-muted-foreground">Collection must be in &quot;ready&quot; status to use Canvas.</div>;
      case 'schedules':
        return <ScheduleManager feedId={currentFeed.id} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Breadcrumb nav */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 group"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform duration-150" />
        <span>Back to collections</span>
      </button>

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold tracking-tight">{currentFeed.name}</h1>
            <StatusBadge status={currentFeed.status} />
          </div>
          <p className="text-muted-foreground mt-1">
            <span className="font-medium text-foreground">{currentFeed.total_products.toLocaleString()}</span> products
            {currentFeed.detected_fields.length > 0 && (
              <> &middot; <span className="font-medium text-foreground">{currentFeed.detected_fields.length}</span> fields</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-lg" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" className="rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Delete
          </Button>
        </div>
      </div>

      {/* Sub-navigation */}
      <div className="flex items-center gap-1.5 p-1 bg-muted/50 rounded-xl border">
        {DATA_SECTIONS.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.key;
          return (
            <button
              key={section.key}
              onClick={() => setActiveSection(section.key)}
              className={`
                flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200
                ${isActive
                  ? 'bg-background text-foreground shadow-sm border'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }
              `}
            >
              <Icon className="h-4 w-4" />
              {section.label}
            </button>
          );
        })}

        <div className="w-px h-6 bg-border mx-1" />

        {CREATIVE_SECTIONS.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.key;
          return (
            <button
              key={section.key}
              onClick={() => setActiveSection(section.key)}
              className={`
                flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200
                ${isActive
                  ? 'bg-background text-foreground shadow-sm border'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }
              `}
            >
              <Icon className="h-4 w-4" />
              {section.label}
            </button>
          );
        })}
      </div>

      {/* Section content */}
      <div>{renderSection()}</div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────

export function FeedWatchPage() {
  const [shops, setShops] = useState<Shop[]>([]);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [selectedFeed, setSelectedFeed] = useState<Feed | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingShop, setIsCreatingShop] = useState(false);

  const loadShops = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await feedWatchService.listShops();
      setShops(result.data);
    } catch {
      // handled
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  const handleCreateShop = async (data: { name: string; platform?: ShopPlatform; shop_url?: string }) => {
    try {
      setIsCreatingShop(true);
      const shop = await feedWatchService.createShop(data);
      setShops((prev) => [shop, ...prev]);
      setSelectedShop(shop);
    } catch {
      // handled
    } finally {
      setIsCreatingShop(false);
    }
  };

  const handleFeedUpdated = (updated: Feed) => {
    setSelectedFeed(updated);
  };

  const handleFeedDeleted = () => {
    setSelectedFeed(null);
  };

  const handleShopDeleted = () => {
    if (selectedShop) {
      setShops((prev) => prev.filter((s) => s.id !== selectedShop.id));
    }
    setSelectedShop(null);
    setSelectedFeed(null);
  };

  const handleBackToShops = () => {
    setSelectedShop(null);
    setSelectedFeed(null);
  };

  const handleBackToShopDetail = () => {
    setSelectedFeed(null);
  };

  // 3-level rendering
  const renderContent = () => {
    if (selectedShop && selectedFeed) {
      return (
        <FeedDetail
          feed={selectedFeed}
          onBack={handleBackToShopDetail}
          onFeedUpdated={handleFeedUpdated}
          onDeleted={handleFeedDeleted}
          shopName={selectedShop.name}
          onBackToShops={handleBackToShops}
        />
      );
    }

    if (selectedShop) {
      return (
        <ShopDetail
          shop={selectedShop}
          onBack={handleBackToShops}
          onSelectFeed={setSelectedFeed}
          onShopDeleted={handleShopDeleted}
        />
      );
    }

    return (
      <ShopList
        shops={shops}
        isLoading={isLoading}
        onSelectShop={setSelectedShop}
        onCreateShop={handleCreateShop}
        onRefresh={loadShops}
        isCreating={isCreatingShop}
      />
    );
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
}
